import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";

export default function FeedbackInbox({ user }) {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    if (user?.id) fetchFeedback();
  }, [user?.id]);

const fetchFeedback = async () => {
  setLoading(true);
  const timeout = setTimeout(() => setLoading(false), 8000); // 8 second safety net
  try {
    // Step 1: get all QR IDs for this user
    const { data: userQRs } = await supabase
      .from("qr_codes")
      .select("id, name")
      .eq("user_id", user.id);

    if (!userQRs?.length) {
      setFeedback([]);
      return;
    }

    const qrIds = userQRs.map(q => q.id);
    const qrMap = {};
    userQRs.forEach(q => { qrMap[q.id] = q.name; });

    // Step 2: get feedback for those QRs OR by user_id
    const { data: feedbackData, error } = await supabase
      .from("feedback")
      .select("*")
      .or(`user_id.eq.${user.id},qr_code_id.in.(${qrIds.join(",")})`)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const enriched = (feedbackData || []).map(item => ({
      ...item,
      campaign_name: qrMap[item.qr_code_id] || "General Scan"
    }));

    setFeedback(enriched);
  } catch (err) {
    console.error("Sync Error:", err.message);
    addToast("Failed to load feedback.", "error");
  } finally {
    clearTimeout(timeout);
    setLoading(false);
  }
};

  // --- NEW: DOWNLOAD CSV FUNCTION ---
  const downloadCSV = () => {
    if (feedback.length === 0) return;

    const headers = ["Date", "Customer", "Email", "Phone", "Rating", "Comment", "Campaign"];
    const rows = feedback.map(item => [
      new Date(item.created_at).toLocaleDateString(),
      item.customer_name || "Guest",
      item.customer_email || item.email || "N/A",
      item.customer_phone || item.phone || "N/A",
      item.rating,
      `"${(item.comment || item.message || "").replace(/"/g, '""')}"`,
      item.campaign_name
    ]);

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `Feedback_Export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="p-20 text-center text-white font-black uppercase tracking-[5px]">Accessing Records...</div>;

  return (
    <div className="p-4 md:p-8 bg-[#020617] min-h-screen text-white font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div>
          <h1 className="text-3xl font-black italic uppercase tracking-tighter leading-none">Feedback Inbox</h1>
          <p className="text-blue-500 text-[10px] font-black uppercase tracking-[5px] mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            Reputation Management Console
          </p>
        </div>
        
        <div className="flex gap-3 w-full md:w-auto">
          <button 
            onClick={downloadCSV} 
            className="bg-blue-600 px-4 py-2 rounded-lg text-[10px] font-bold uppercase"
          >
            Download CSV
          </button>
          <button 
            onClick={fetchFeedback} 
            className="flex-1 md:flex-none bg-white text-black px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-xl active:scale-95"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {feedback.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-24 rounded-[56px] text-center backdrop-blur-md">
          <p className="text-slate-500 text-sm font-bold uppercase tracking-widest">No feedback records found.</p>
        </div>
      ) : (
        <div className="grid gap-8">
          {feedback.map((item) => {
            const isNegative = item.rating <= 3;
            // FORCED EXTRACTION: Checks every possible naming convention
            const email = item.customer_email || item.email || "No Email";
            const phone = item.customer_phone || item.phone || "No Phone";

            return (
              <div key={item.id} className={`p-8 md:p-10 rounded-[48px] border transition-all duration-500 ${isNegative ? 'bg-red-500/[3%] border-red-500/20 shadow-lg' : 'bg-white/[02] border-white/10'}`}>
                <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <h3 className="text-2xl font-black uppercase italic text-white leading-none">{item.customer_name || "Guest User"}</h3>
                        {isNegative && <span className="bg-red-500 text-[8px] font-black uppercase px-2 py-1 rounded-md animate-pulse">Action Required</span>}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="text-blue-400 text-[10px] font-black px-4 py-1.5 bg-blue-400/5 rounded-full border border-blue-400/10 uppercase tracking-tighter">
                        {email}
                      </span>
                      <span className="text-emerald-400 text-[10px] font-black px-4 py-1.5 bg-emerald-400/5 rounded-full border border-emerald-400/10 uppercase tracking-tighter">
                        {phone}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-left md:text-right">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={`text-xl ${i < item.rating ? "text-yellow-400" : "text-white/5"}`}>★</span>
                      ))}
                    </div>
                    <span className="text-[10px] font-black uppercase text-slate-600 tracking-widest">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className={`p-8 rounded-3xl border text-sm leading-relaxed ${isNegative ? 'bg-black/60 border-red-500/10 text-white italic' : 'bg-white/[01] border-white/5 text-slate-400'}`}>
                  "{item.comment || item.message || "No comment provided."}"
                </div>

                <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap justify-between items-center gap-4 text-[10px] font-black uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Campaign:</span>
                    <span className="text-white bg-white/5 px-3 py-1 rounded-lg">{item.campaign_name}</span>
                  </div>
                  
                  {isNegative && (phone !== "No Phone") && (
                    <a href={`tel:${phone}`} className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping"/> Resolve Conflict
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}